import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  Typography,
} from "@mui/material";
import Header from "../../components/Header";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import SendIcon from "@mui/icons-material/Send";
import axios from "axios";
import * as yup from "yup";
import { addAdminAction } from "../../redux/actions/adminAction";
import { toast } from "react-toastify";
import LoadingIndicator from "../../components/loadingIndicator";
import { useGetRolesQuery } from "../../state/api";

const checkUsernameAvailability = async (value) => {
  try {
    const response = await axios.get(
      `/admin/checkAdminUsername?username=${value}`
    );
    const exists = response.data.exists;
    return exists;
  } catch (error) {
    console.error(error);
    return false;
  }
};

const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const validationSchema = yup.object().shape({
  username: yup
    .string()
    .required("Username is required")
    .test(
      "valid-characters",
      "Username can only contain letters, numbers, and @,$,-,_",
      function (value) {
        return /^[a-zA-Z0-9@,$\-_]*$/.test(value);
      }
    )
    .test("length", "Username must be between 4 and 12 characters", (value) => {
      return value.length >= 4 && value.length <= 12;
    })
    .test("is-unique", "Username already exists", async function (value) {
      // Check if username already exists
      try {
        const exists = await checkUsernameAvailability(value);
        console.log("Exists:", exists); // Log to check if the value is retrieved correctly
        return !exists; // Resolve to true if the username doesn't exist (unique)
      } catch (error) {
        console.error("Validation Error:", error); // Log any validation errors
        return false; // Return false in case of an error
      }
    }),
  mobileNumber: yup
    .string()
    .matches(/^[0-9]+$/, "Mobile number must contain only digits")
    .min(10, "Mobile number must be at least 10 digits")
    .max(15, "Mobile number can be maximum 15 digits")
    .required("Mobile number is required"),
  fullname: yup.string().required("Full name is required"),
  email: yup
    .string()
    .matches(emailRegex, "Invalid email format")
    .required("Email is required"),
  password: yup
    .string()
    // .min(8, "Password must be at least 8 characters")
    // .max(15, "Password must be less than 15 characters")
    // .matches(
    //   /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,12}$/,
    //   "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
    // )
    .required("Password is required"),
  isActive: yup.string().required("Select a status"),
  role: yup.string().required("Role is required"),
  roleId: yup.string().required("Role is required"),
});

const CreateAdmin = () => {
  const [err, setErr] = useState({});
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullname: "",
    email: "",
    username: "",
    password: "",
    mobileNumber: "",
    isActive: "",
    role: "",
    roleId: "",
  });

  const handleBlur = async (e) => {
    const fieldName = e.target.name;
    try {
      await validationSchema.validateAt(fieldName, formData);
      setErr({ ...err, [fieldName]: "" });
    } catch (error) {
      setErr({ ...err, [fieldName]: error.message });
    }
  };

  const { data: roleData = [] } = useGetRolesQuery();

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => {
      let updatedData = {
        ...prevData,
        [name]: value,
      };

      if (name === "roleId") {
        const selectedRole = roleData.find((role) => role._id === value);
        updatedData = {
          ...updatedData,
          role: selectedRole ? selectedRole.title : "",
        };
      }

      return updatedData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true); // Start loading
      // await new Promise((resolve) =>
      //   setTimeout(resolve, Math.random() * 1000 + 2000)
      // );
      await validationSchema.validate(formData, { abortEarly: false });

      // Dispatch the action with the formDataToSend
      const isSuccess = await dispatch(addAdminAction(formData));

      // for (const [key, value] of formDataToSend.entries()) {
      //   console.log(key, value);
      // }
      console.log("formData:", formData);

      // Handle success or failure
      if (isSuccess) {
        toast.success("Admin added successfully");
        navigate("/admin");
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        toast.error("Something went wrong");
      }
    } catch (validationErrors) {
      console.error("Validation error occurred:", validationErrors);
      // Handle validation errors
      const errors = {};
      validationErrors.inner.forEach((validationError) => {
        errors[validationError.path] = validationError.message;
      });
      setErr(errors);
    } finally {
      setLoading(false); // Stop loading
    }
  };

  return (
    <Box mt="1.5rem" mb="2.5rem" ml="2.5rem" mr="2.5rem">
      <Header title="ADD ADMIN" />
      <form onSubmit={handleSubmit}>
        <FormControl fullWidth variant="outlined" margin="normal">
          {/* Full Name */}
          <TextField
            label="Full Name"
            name="fullname"
            value={formData.fullname}
            onChange={handleChange}
            onBlur={handleBlur}
            fullWidth
            margin="normal"
          />
          {err.fullname && (
            <Grid item>
              <Typography color="error">{err.fullname}</Typography>
            </Grid>
          )}
          {/* Email */}
          <TextField
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            fullWidth
            margin="normal"
          />
          {err.email && (
            <Grid item>
              <Typography color="error">{err.email}</Typography>
            </Grid>
          )}
          {/* Username */}
          <TextField
            label="Username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            onBlur={handleBlur}
            fullWidth
            margin="normal"
          />
          {err.username && (
            <Grid item>
              <Typography color="error">{err.username}</Typography>
            </Grid>
          )}
          {/* Password */}
          <TextField
            label="Password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            onBlur={handleBlur}
            fullWidth
            margin="normal"
          />
          {err.password && (
            <Grid item>
              <Typography color="error">{err.password}</Typography>
            </Grid>
          )}
          {/* Mobile Number */}
          <TextField
            label="Mobile Number"
            name="mobileNumber"
            value={formData.mobileNumber}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyPress={(e) => {
              const onlyNums = e.target.value.replace(/[^0-9]/g, "");
              if (e.target.value !== onlyNums) {
                e.target.value = onlyNums;
              }
            }}
            fullWidth
            margin="normal"
          />
          {err.mobileNumber && (
            <Grid item>
              <Typography color="error">{err.mobileNumber}</Typography>
            </Grid>
          )}
          {/* IsActive */}
          <FormControl fullWidth variant="outlined" margin="normal">
            <div style={{ display: "flex", alignItems: "center" }}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Is Active</InputLabel>
                <Select
                  name="isActive"
                  value={formData.isActive}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <MenuItem value="true">True</MenuItem>
                  <MenuItem value="false">False</MenuItem>
                </Select>{" "}
                {err.isActive && (
                  <Grid item>
                    <Typography color="error">{err.isActive}</Typography>
                  </Grid>
                )}
              </FormControl>
            </div>
          </FormControl>
          {/* Role */}
          <FormControl
            fullWidth
            variant="outlined"
            margin="normal"
            error={!!err.role}
          >
            <InputLabel htmlFor="role">Roles</InputLabel>
            <Select
              name="roleId"
              value={formData.roleId}
              onChange={handleChange}
              onBlur={handleBlur}
            >
              {roleData.map((role) => (
                <MenuItem key={role._id} value={role._id}>
                  {role.title}
                </MenuItem>
              ))}
            </Select>
            {err.roleId &&
              formData.roleId.length === 0 && ( // Only display the error if no role is selected
                <Typography variant="caption" color="error">
                  {err.roleId}
                </Typography>
              )}
          </FormControl>
          {/* SUBMIT BUTTON */}
          <Grid container justifyContent="center" alignItems="center">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              disabled={loading}
            >
              Submit
            </Button>
          </Grid>
        </FormControl>
      </form>
      {/* Render loading indicator if loading */}
      {loading && <LoadingIndicator />}
    </Box>
  );
};

export default CreateAdmin;
